import React, { useState } from "react";
import { FiX, FiUpload, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import adminService from "../services/adminService";
import { toast } from "react-toastify";

const UploadModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return toast.error("Please select a file");

    try {
      setUploading(true);
      setResult(null);
      const res = await adminService.bulkUploadMarketplaceInfluencers(file);

      if (res.success) {
        const summary = res.data?.summary;
        setResult({ message: res.message, summary, full: res.data });
        toast.success(res.message || "Upload processed");
        if (summary?.failed > 0) {
          toast.warning(`${summary.failed} row(s) failed — expand details below.`);
        }
        onSuccess();
      }
    } catch (err) {
      toast.error(err.message || err.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setFile(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] flex flex-col">

        <div className="flex justify-between items-center px-6 py-4 border-b shrink-0">
          <h2 className="text-lg font-semibold">Upload Influencers</h2>
          <button type="button" onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600">
            New rows are created when the username is not in the database. Existing usernames get only
            <span className="font-medium"> empty fields</span> filled from the sheet (email conflicts are skipped).
          </p>

          <label className="block text-sm font-medium text-gray-700">
            Select Excel File (.xlsx, .xls, .csv)
          </label>

          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              setFile(e.target.files[0]);
              setResult(null);
            }}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
          />

          {file && !result && (
            <p className="text-sm text-gray-500">
              Selected: <span className="font-medium">{file.name}</span>
            </p>
          )}

          {result?.summary && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
              <div className="flex items-center gap-2 text-green-700 font-medium">
                <FiCheckCircle size={18} />
                Import finished
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                <dt className="text-gray-600">Created</dt>
                <dd className="font-medium text-right">{result.summary.created}</dd>
                <dt className="text-gray-600">Updated (filled gaps)</dt>
                <dd className="font-medium text-right">{result.summary.updated}</dd>
                <dt className="text-gray-600">Unchanged</dt>
                <dd className="font-medium text-right">{result.summary.unchanged}</dd>
                <dt className="text-gray-600">Failed</dt>
                <dd className="font-medium text-right text-red-700">{result.summary.failed}</dd>
              </dl>
              {result.full?.failed?.length > 0 && (
                <div className="pt-2 border-t border-gray-200">
                  <p className="flex items-center gap-1 text-red-800 font-medium mb-2">
                    <FiAlertCircle size={16} />
                    Failed rows
                  </p>
                  <ul className="max-h-32 overflow-y-auto text-xs text-gray-700 space-y-1 font-mono">
                    {result.full.failed.slice(0, 25).map((f, i) => (
                      <li key={`${f.row}-${i}`}>
                        Row {f.row}: {f.reason}
                      </li>
                    ))}
                    {result.full.failed.length > 25 && (
                      <li className="text-gray-500">…and {result.full.failed.length - 25} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            {result ? "Close" : "Cancel"}
          </button>

          {!result && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? (
                "Uploading..."
              ) : (
                <>
                  <FiUpload size={16} />
                  Upload
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(UploadModal);
